import { ViewIds } from '@ardoq/api-types';
import { createSvgElement } from '@ardoq/dom-utils';
import { logWarn } from '@ardoq/logging';
import {
  DEVICE_PIXEL_RATIO,
  HTML_EXPORT_LEGEND_PADDING,
  HTML_EXPORT_PADDING,
  ONLY_EXPORT_CHILDREN_SELECTOR,
  VIEW_LEGEND_SELECTOR,
} from './consts';
import {
  EXPORT_AUTOSIZE_CLASS_NAME,
  NO_EXPORT_SELECTOR,
  VISUALIZATION_CONTENT_CONTAINER_CLASS_NAME,
  X_AXIS_CONTAINER_CLASS_NAME,
} from '@ardoq/global-consts';
import { IMAGE_COLOR_FILTERS_CONTAINER_ID } from '@ardoq/color-helpers';
import {
  EXPORT_FORMAT,
  ElementStyleAttributesHandler,
  ExportLabels,
  ExportTrackingData,
  ExportedViewMetadata,
  Point,
} from './types';
import { alert, confirm } from '@ardoq/modal';
import {
  getBrowserName,
  getBrowserPlatform,
  getBrowserVersionNumber,
} from '@ardoq/common-helpers';
import * as profiling from '@ardoq/profiling';
import {
  isExportCapturing,
  reportExportFailure,
  toExportTooLargeError,
} from './exportSink';

export const elementToSvgString = (
  node: Element,
  width: number,
  height: number,
  defs?: string,
  position?: Point
) => {
  const insertCoords = position ? `x="${position[0]}" y="${position[1]}" ` : '';

  return `<svg ${insertCoords}width="${width}px" height="${height}px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    ${defs ? `<defs>${defs}</defs>` : ''}
    <foreignObject width="100%" height="100%">
        ${new window.XMLSerializer().serializeToString(node)}
    </foreignObject>
  </svg>`;
};

export const getImageDataURI = (uri: string) =>
  new Promise<string>(resolve => {
    const canvasImage = new Image();
    canvasImage.crossOrigin = 'anonymous';
    const canvas = createExportCanvas();
    const imgHandler = async () => {
      const width = canvasImage.width;
      const height = canvasImage.height;
      canvas.width = width * DEVICE_PIXEL_RATIO;
      canvas.height = height * DEVICE_PIXEL_RATIO;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(DEVICE_PIXEL_RATIO, DEVICE_PIXEL_RATIO);
        try {
          ctx.drawImage(canvasImage, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/png');
          // handle broken image
          if (dataUrl === 'data:,') {
            resolve(await getFallbackImageDataURI(uri));
          } else {
            resolve(dataUrl);
          }
        } catch (e) {
          logWarn(e as Error, 'Failed to load image in export');
          resolve(await getFallbackImageDataURI(uri));
        }
        canvas.remove();
      }
    };

    canvas.style.position = 'absolute';
    canvas.style.visibility = 'hidden';
    document.body.prepend(canvas);

    canvasImage.crossOrigin = 'Anonymous';
    canvasImage.onload = imgHandler;
    canvasImage.onerror = imgHandler;
    canvasImage.src = uri;
  });

const getFallbackImageDataURI = async (uri: string) => {
  const fallbackImage = '/img/ardoq_icon.png';
  if (uri !== fallbackImage) {
    return getImageDataURI(fallbackImage);
  }
  // Empty image if fallbackImage failed to load
  return 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
};
const setImageData = async (
  node: HTMLImageElement,
  clonedNode: HTMLImageElement
) => {
  try {
    const dataURI = await getImageDataURI(node.src);
    clonedNode.setAttribute('src', dataURI as string);
    clonedNode.setAttribute('crossorigin', 'anonymous');
  } catch (error) {
    logWarn(error as Error, 'Failed to load image in export');
  }
};

const setViewLegendContainerElementStyles = (
  originalContainerElement: HTMLElement,
  clonedDOM: HTMLElement
) => {
  const clonedLegendContainer =
    clonedDOM.querySelector<HTMLElement>(VIEW_LEGEND_SELECTOR);

  const clonedContentContainer = clonedDOM.querySelector<HTMLElement>(
    `.${VISUALIZATION_CONTENT_CONTAINER_CLASS_NAME}`
  );
  if (clonedContentContainer) {
    clonedContentContainer.style.setProperty('height', 'fit-content');
  }

  if (clonedLegendContainer) {
    const contentNode = originalContainerElement.querySelector<HTMLElement>(
      `.${VISUALIZATION_CONTENT_CONTAINER_CLASS_NAME}`
    );

    const axisNode = originalContainerElement.querySelector<HTMLElement>(
      `.${X_AXIS_CONTAINER_CLASS_NAME}`
    );

    const legendWidth =
      originalContainerElement.querySelector(VIEW_LEGEND_SELECTOR)
        ?.clientWidth ?? 0;

    const contentWidth = contentNode ? contentNode.clientWidth : 0;
    const axisWidth = axisNode ? axisNode.clientWidth : contentWidth;
    const axisOffset = axisNode ? axisNode.offsetHeight : 0;
    const rightOffset =
      contentWidth - axisWidth + legendWidth + HTML_EXPORT_PADDING;
    const leftOffset = contentWidth + HTML_EXPORT_LEGEND_PADDING;

    clonedLegendContainer.style.setProperty('height', 'fit-content');
    clonedLegendContainer.style.setProperty('top', `${axisOffset}px`);

    // Extend View width
    clonedLegendContainer.style.setProperty('right', `-${rightOffset}px`);
    // Legend placement
    clonedLegendContainer.style.setProperty('left', `${leftOffset}px`);
  }
};

const cloneTextContentPseudoElement = (
  node: HTMLElement | SVGElement,
  clonedNode: HTMLElement | SVGElement
) => {
  const pseudoElement = window.getComputedStyle(node, ':before');
  if (pseudoElement.getPropertyValue('content') !== 'none') {
    clonedNode.prepend(
      document.createTextNode(
        `  ${pseudoElement.getPropertyValue('content').replace(/"/g, '')}  `
      )
    );
  }
};
const hasPseudoElements = (element: Element) =>
  window.getComputedStyle(element, ':after').getPropertyValue('content') !==
    'none' ||
  window.getComputedStyle(element, ':before').getPropertyValue('content') !==
    'none';

const clonePseudoElementsOfElement = (
  node: HTMLElement,
  clonedNode: HTMLElement
) => {
  const pseudoElementPositions = [':before', ':after'];

  pseudoElementPositions.forEach(pseudoElementPosition => {
    const pseudoElementStyle = window.getComputedStyle(
      node,
      pseudoElementPosition
    );
    const mockElement = document.createElement('div');
    Array.from(pseudoElementStyle).forEach(key => {
      mockElement.style.setProperty(
        key,
        pseudoElementStyle.getPropertyValue(key),
        pseudoElementStyle.getPropertyPriority(key)
      );
    });

    clonedNode.append(mockElement);
  });
};
const isElementType = (node: Element, type: string) =>
  node.nodeName.toLowerCase() === type;

const basicSvgShapes = new Set([
  'ellipse',
  'path',
  'rect',
  'circle',
  'line',
  'polyline',
  'polygon',
]);

const isSvgType = (node: Element) =>
  isElementType(node, 'svg') ||
  isElementType(node, 'g') ||
  isElementType(node, 'path');

/** window.getComputedStyle returns the used values for these css properties, not the computed values.  https://drafts.csswg.org/cssom/#resolved-values */
const USED_VALUE_KEYS = [
  'width',
  'height',
  'block-size',
  'inline-size',
  'top',
  'bottom',
  'left',
  'right',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'margin-inline',
];

const copyStyle = (
  node: HTMLElement | SVGElement,
  clonedNode: HTMLElement | SVGElement
) => {
  const computedStyle = window.getComputedStyle(node);
  Array.from(computedStyle).forEach(key => {
    if (key !== 'cssText') {
      clonedNode.style.setProperty(
        key,
        computedStyle.getPropertyValue(key),
        computedStyle.getPropertyPriority(key)
      );
    }
  });
};

const hasClass = (node: Element, className: string) =>
  node.classList.value.includes(className);

type AppendChildrenArgs = {
  parentNode: Element;
  clonedNode: Element;
  viewType: string;
  deferredLoadedImages: Promise<void>[];
  elementStyleAttributesHandler: ElementStyleAttributesHandler;
  mutableTrackingData: ExportTrackingData;
};
const appendChildren = ({
  parentNode,
  clonedNode,
  viewType,
  deferredLoadedImages,
  elementStyleAttributesHandler,
  mutableTrackingData,
}: AppendChildrenArgs) => {
  Array.from(parentNode.childNodes).forEach(child =>
    clonedNode.appendChild(
      recursiveTraversal({
        mutableTrackingData,
        node: child,
        viewType,
        deferredLoadedImages,
        elementStyleAttributesHandler,
      })
    )
  );
};

const copySVGAttributes = (source: SVGElement, target: SVGElement) => {
  [...source.attributes].forEach(({ localName, name, value }) => {
    if (localName === 'transform' || localName === 'href') {
      return;
    }
    target.setAttribute(name, value);
  });
};

/**
 * Sets style properties only relevant for SVG elements.
 */
const setStylePropertiesForClonedSVGElement: ElementStyleAttributesHandler = ({
  node,
  clonedNode,
  viewType,
}) => {
  if (!(node instanceof SVGElement && clonedNode instanceof SVGElement)) {
    return;
  }

  if (
    viewType !== 'viewLegend' ||
    !basicSvgShapes.has(node.tagName.toLowerCase())
  ) {
    copyStyle(node, clonedNode);
  }
  if (node.classList.contains('CUSTOM_SVG_ICON_SELECTOR')) {
    clonedNode.style.setProperty('width', '100%');
  }
};

type CloneSVGUseElementsArgs = {
  node: SVGUseElement;
  deferredLoadedImages: Promise<void>[];
  viewType: string;
  mutableTrackingData: ExportTrackingData;
};
/**
 * Resolves the corresponding element in the shadow dom and returns the cloned node
 * with all the descendants.
 *
 * Returns null if document has no elements for this node.href.baseVal value.
 */
const cloneSVGUseElements = ({
  node,
  deferredLoadedImages,
  viewType,
  mutableTrackingData,
}: CloneSVGUseElementsArgs) => {
  // the referenced element is loaded into the shadow dom
  const svgNode = document.body.querySelector<SVGElement>(node.href.baseVal);
  let clonedNode: SVGElement | null = null;

  if (svgNode) {
    const clonedSvgNode = svgNode.cloneNode() as SVGElement;

    const transform = node.getAttribute('transform'); // Needed to reposition the children correctly
    if (transform) {
      // transform isn't supported on an SVG element until SVG 2.0, which is not yet implemented in chrome.
      // therefore, if a transform was present on the <use>, we need to wrap the <svg> in a <g> with the transform applied to it.
      clonedNode = createSvgElement('g', {
        transform,
      });

      // #region apply width and height from <use> element to new <svg> element.
      // the <use> element might have had a width and height set. these will be ineffective on the <g> element, so we must copy them to the <svg> element.
      const width = node.getAttribute('width');
      const height = node.getAttribute('height');
      if (width) {
        clonedSvgNode.setAttribute('width', width);
      }
      if (height) {
        clonedSvgNode.setAttribute('height', height);
      }
      // #endregion
      clonedNode.appendChild(clonedSvgNode);
    } else if (svgNode instanceof SVGSymbolElement) {
      const newSvgContainerGroup = createSvgElement('svg');
      appendChildren({
        mutableTrackingData,
        parentNode: svgNode.cloneNode(true) as SVGElement,
        clonedNode: newSvgContainerGroup,
        deferredLoadedImages,
        viewType,
        elementStyleAttributesHandler: setStylePropertiesForClonedSVGElement,
      });
      // copy all attributes except transform and href from the <symbol> element to the cloned <svg> element.
      copySVGAttributes(svgNode, newSvgContainerGroup);

      clonedNode = newSvgContainerGroup as SVGElement;
    } else {
      clonedNode = clonedSvgNode;
    }

    // copy all attributes except transform and href from the <use> element to the cloned <svg> element.
    copySVGAttributes(node, clonedNode as SVGElement);

    appendChildren({
      mutableTrackingData,
      parentNode: svgNode,
      clonedNode: clonedSvgNode,
      deferredLoadedImages,
      viewType,
      elementStyleAttributesHandler: setStylePropertiesForClonedSVGElement,
    });
  }
  return clonedNode;
};

type RecursiveTraversalArgs = {
  node: Node;
  deferredLoadedImages: Promise<void>[];
  elementStyleAttributesHandler: ElementStyleAttributesHandler;
  viewType: string;
  mutableTrackingData: ExportTrackingData;
  rootContainerNode?: HTMLElement;
};

const toCanvasSnapshot = (
  canvas: HTMLCanvasElement
): HTMLImageElement | null => {
  try {
    const image = document.createElement('img');
    image.src = canvas.toDataURL();
    copyStyle(canvas, image);
    return image;
  } catch {
    return null;
  }
};

export const recursiveTraversal = ({
  node,
  viewType,
  deferredLoadedImages,
  elementStyleAttributesHandler,
  rootContainerNode,
  mutableTrackingData,
}: RecursiveTraversalArgs) => {
  mutableTrackingData.numberOfManuallyClonedDOMNodes += 1;

  const clonedNode = node.cloneNode();

  const canvasSnapshot =
    node instanceof HTMLCanvasElement && isExportCapturing()
      ? toCanvasSnapshot(node)
      : null;
  if (canvasSnapshot) {
    rootContainerNode?.appendChild(canvasSnapshot);
    return canvasSnapshot;
  }

  if (node instanceof HTMLImageElement) {
    deferredLoadedImages.push(
      setImageData(node, clonedNode as HTMLImageElement)
    );
    copyStyle(node, clonedNode as HTMLImageElement);
    return clonedNode;
  }

  if (node instanceof SVGUseElement) {
    return (
      cloneSVGUseElements({
        mutableTrackingData,
        node,
        deferredLoadedImages,
        viewType,
      }) || clonedNode
    );
  }

  if (
    (node instanceof HTMLElement && clonedNode instanceof HTMLElement) ||
    (node instanceof SVGElement && clonedNode instanceof SVGElement)
  ) {
    if (rootContainerNode) {
      rootContainerNode.appendChild(clonedNode);
    }

    elementStyleAttributesHandler({ node, clonedNode, viewType });

    appendChildren({
      mutableTrackingData,
      parentNode: node,
      clonedNode,
      viewType,
      deferredLoadedImages,
      elementStyleAttributesHandler,
    });
  }

  return clonedNode;
};

/**
 * This legacy function should be decomposed in favor of view-specific or cathegory-specific
 * ElementStyleAttributesHandlers, which would be passed as arguments to recursiveTraversal
 * according to cases.
 *
 * When this function is decomposed, the viewType argument should become obsolete.
 */
const setAttributesForClonedElement: ElementStyleAttributesHandler = ({
  node,
  clonedNode,
  viewType,
}) => {
  // copy styles while preserving svg shapes within the legend
  if (!isSvgType(node) || viewType === 'viewLegend') {
    if (!basicSvgShapes.has(node.tagName.toLowerCase())) {
      copyStyle(node, clonedNode);
      if (viewType === ViewIds.TIMELINE) {
        const computedStyle = window.getComputedStyle(node);
        /**
         * Replace 'flow-root' with '-webkit-box' when exporting Timeline view.
         * We need '-webkit-box' for proper text truncation with ellipsis in exported files.
         */
        if (computedStyle.getPropertyValue('display') === 'flow-root') {
          clonedNode.style.setProperty('display', '-webkit-box');
        }
      }
    }
  }
  if (
    node instanceof SVGElement &&
    clonedNode instanceof SVGElement &&
    viewType !== 'viewLegend'
  ) {
    copyStyle(node, clonedNode);
  }

  // special needs
  if (
    viewType !== ViewIds.TIMELINE &&
    node.scrollHeight >
      parseInt(clonedNode.style.getPropertyValue('height'), 10)
  ) {
    clonedNode.style.setProperty('height', `${node.scrollHeight}px`);
  } else if (
    viewType === ViewIds.DEPMATRIX &&
    !hasClass(node, 'columnHeader')
  ) {
    if (isElementType(node, 'td')) {
      clonedNode.style.setProperty('border-inline', '1px solid #D6D9E1');
    }
  } else if (viewType === ViewIds.TABLEVIEW && isElementType(node, 'li')) {
    cloneTextContentPseudoElement(node, clonedNode);
  } else if (
    (viewType === ViewIds.TABLEVIEW || viewType === ViewIds.ROADMAP) &&
    (isElementType(node, 'td') || isElementType(node, 'th'))
  ) {
    clonedNode.style.setProperty('border-inline', '1px solid #ddd');
  }

  // "finally" for instanceof Element
  if (clonedNode.classList.contains('CUSTOM_SVG_ICON_SELECTOR')) {
    clonedNode.style.setProperty('width', '100%');
  }
  if (hasClass(node, EXPORT_AUTOSIZE_CLASS_NAME)) {
    // things like width and height shouldn't be set explicitly on every element. it wreaks havoc on flexbox sizing.
    // set these values back to what they were on the node style.
    USED_VALUE_KEYS.forEach(key => {
      clonedNode.style.setProperty(key, node.style.getPropertyValue(key));
    });
  }
  if (
    (ViewIds.TIMELINE === viewType || ViewIds.TABLEVIEW === viewType) &&
    /* only ViewIds.TIMELINE and ViewIds.TABLEVIEW to not accidentally break something in other views, adjust as needed */
    hasPseudoElements(node) &&
    node instanceof HTMLElement &&
    clonedNode instanceof HTMLElement
  ) {
    clonePseudoElementsOfElement(node, clonedNode);
  }
};

export const cloneImageColorFilters = () => {
  const currentImageColorFilters = document.querySelector<SVGElement>(
    `#${IMAGE_COLOR_FILTERS_CONTAINER_ID}`
  );
  return currentImageColorFilters
    ? (currentImageColorFilters.cloneNode(true) as SVGElement).innerHTML
    : null;
};

export const removeUnwantedNodes = (clonedDOM: HTMLElement | SVGElement) => {
  const legend = clonedDOM.querySelector('g.legend');
  if (legend && legend.classList.contains('visible')) {
    legend.remove();
  }
  [...Array.from(clonedDOM.querySelectorAll(NO_EXPORT_SELECTOR))].forEach(
    node => node.remove()
  );
  [
    ...Array.from(clonedDOM.querySelectorAll(ONLY_EXPORT_CHILDREN_SELECTOR)),
  ].forEach(node => {
    node.replaceWith(...node.childNodes);
  });
};

export const getClonedDOM = async (
  containerElement: HTMLElement,
  viewType: string,
  mutableTrackingData: ExportTrackingData,
  profilingTransaction: profiling.Transaction
) => {
  const getClonedDOMSpan = profiling.startSpan(
    profilingTransaction,
    'getClonedDOM and set styles on it: this could be a legend in SVG or an HTML view body'
  );

  const deferredLoadedImages: Promise<void>[] = [];
  const clonedDOM = recursiveTraversal({
    mutableTrackingData,
    node: containerElement,
    viewType,
    deferredLoadedImages,
    elementStyleAttributesHandler: setAttributesForClonedElement,
  }) as HTMLElement;

  setViewLegendContainerElementStyles(containerElement, clonedDOM);

  profiling.endSpan(getClonedDOMSpan);

  const getClonedDOMReadySpan = profiling.startSpan(
    profilingTransaction,
    'getClonedDOM: remove unwanted nodes and wait for images to load'
  );

  removeUnwantedNodes(clonedDOM);
  await Promise.all(deferredLoadedImages);

  profiling.endSpan(getClonedDOMReadySpan);

  clonedDOM.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  clonedDOM.style.overflow = 'visible';
  clonedDOM.style.setProperty('width', 'fit-content');
  clonedDOM.style.setProperty('height', 'fit-content');
  return clonedDOM;
};
export const createExportCanvas = () => {
  const canvas = document.createElement('canvas');
  canvas.id = 'canvasExport';
  return canvas;
};

export const getUnableToExportMessage = (
  format: string,
  labels?: ExportLabels
) => {
  if (labels?.unableToGenerateMessage) {
    return labels.unableToGenerateMessage(format);
  }
  return `Try to generate the ${format} again using a smaller
    data set. If the issue keeps happening, please contact customer
    support.`;
};

export const confirmLargeFile = async (labels?: ExportLabels) => {
  if (isExportCapturing()) {
    reportExportFailure(
      toExportTooLargeError('Large export needs a confirmation')
    );
    return false;
  }
  return await confirm({
    title: labels?.largeFileWarningTitle ?? 'Warning',
    subtitle:
      labels?.largeFileWarningSubtitle ??
      'This export will result in a very large file. This might create unexpected results.',
  });
};

export const notifyExportFailure = async (
  format: EXPORT_FORMAT,
  labels?: ExportLabels
) => {
  const formatInUppercase = format.toLocaleUpperCase();
  alert({
    title:
      labels?.unableToGenerateTitle?.(formatInUppercase) ??
      `Unable to generate ${formatInUppercase}`,
    errorMessage: getUnableToExportMessage(formatInUppercase, labels),
    confirmButtonTitle: labels?.closeButton ?? 'Close',
  });
};

export const composeExportMetadata = (
  {
    name,
    isViewpointMode,
    isDiscoverViewpoint = false,
    getNumberOfComponents,
    getNumberOfReferences,
  }: ExportedViewMetadata,
  exportFormat: EXPORT_FORMAT
) => ({
  name,
  isViewpointMode,
  isDiscoverViewpoint,
  numberOfComponents: getNumberOfComponents?.() ?? 'N/A',
  numberOfReferences: getNumberOfReferences?.() ?? 'N/A',
  format: exportFormat,
  browserPlattform: getBrowserPlatform() ?? 'N/A',
  browserVersionNumber: getBrowserVersionNumber() ?? 'N/A',
  browserName: getBrowserName() ?? 'N/A',
  numberOfManuallyClonedDOMNodes: 0,
});

export const getExportProfilingTransaction = () =>
  profiling.startTransaction(
    'export visualization',
    17000, // usually it would take around 20 seconds for page unresponsive timeout pop up
    profiling.Team.INSIGHT
  );
